import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

import ChonDiaChi from "components/ChonDiaChi";
import TieuDeNhomForm, { coNoiDung, useNhomFormMo } from "components/TieuDeNhomForm";
import { danhMucApi } from "api/danhMuc";

/** Mức diện tích nguyên (ha) chọn nhanh. */
const MUC_DIEN_TICH = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const THU_TU_NHOM = ["dinhDanh", "diaChi", "canhTac", "chungNhan", "ghiChu"];
const NHOM_TUY_CHON = ["chungNhan", "ghiChu"];

/**
 * Format số thập phân kiểu VN (dấu phẩy) để hiển thị trong tên vườn / ô nhập.
 * 9 → "9", 9.5 → "9,5"
 */
export function formatSoThapPhanVn(giaTri) {
  if (giaTri === "" || giaTri == null) return "";
  const chuoi = String(giaTri).trim().replace(",", ".");
  if (chuoi === "" || chuoi === ".") return chuoi === "." ? "0," : "";
  if (chuoi.endsWith(".")) return `${chuoi.slice(0, -1)},`;
  const so = Number(chuoi);
  if (Number.isNaN(so)) return "";
  return String(so).replace(".", ",");
}

/**
 * Parse ô diện tích (chấp nhận "9,5" hoặc "9.5") → number hoặc "" (rỗng / đang gõ dở).
 * Giữ string kết thúc bằng "." khi đang nhập thập phân để Number() ở parent vẫn ổn.
 */
export function parseDienTichHa(raw) {
  if (raw === "" || raw == null) return "";
  let s = String(raw)
    .trim()
    .replace(/[^\d.,]/g, "");
  if (!s) return "";
  // Chỉ giữ một dấu thập phân (ưu tiên dấu cuối cùng).
  const dauPhay = s.lastIndexOf(",");
  const dauCham = s.lastIndexOf(".");
  const idx = Math.max(dauPhay, dauCham);
  if (idx >= 0) {
    const nguyen = s.slice(0, idx).replace(/[.,]/g, "");
    const thapPhan = s.slice(idx + 1).replace(/[.,]/g, "");
    s =
      thapPhan.length > 0 || s.endsWith(",") || s.endsWith(".") ? `${nguyen}.${thapPhan}` : nguyen;
  }
  if (s === "" || s === ".") return s === "." ? "0." : "";
  if (s.endsWith(".")) return s;
  const so = Number(s);
  return Number.isNaN(so) ? "" : so;
}

/** Ghép tên vườn: "Họ tên 9,5 ha". Không có họ tên → "". */
export function taoTenVuonTuChuVaDienTich(hoTen, dienTichHa) {
  const ten = (hoTen ?? "").trim();
  if (!ten) return "";
  const so = parseDienTichHa(dienTichHa);
  if (so === "" || so === "." || so === "0.") {
    return ten;
  }
  const hienThi = formatSoThapPhanVn(so);
  if (!hienThi) return ten;
  return `${ten} ${hienThi} ha`;
}

/**
 * Kiểm tra field bắt buộc trước khi lưu vườn (admin + hiện trường).
 * Ném Error với danh sách thiếu — UI/API dùng chung.
 * @deprecated Dùng `lib/thongTinVuon` — re-export để import cũ không gãy.
 */
export { kiemTraThongTinVuon } from "lib/thongTinVuon";

function phanNguyenDienTich(dienTichHa) {
  const so = Number(String(dienTichHa ?? "").replace(",", "."));
  if (Number.isNaN(so) || so <= 0) return null;
  return Math.floor(so);
}

// Form đầy đủ thông tin Vườn — dùng chung cho màn Quản lý (admin) và luồng
// thu mua ngoài hiện trường (nhân viên).
//
// Thứ tự trường (nghiệp vụ):
// 1. Định danh — diện tích → tên vườn tự ghép (họ tên chủ + diện tích)
// 2. Địa chỉ — cùng chủ / khác + tỉnh/xã/thôn/chi tiết
// 3. Canh tác — giống bơ (bắt buộc) + năm trồng + số cây
// 4. Chứng nhận — mã vùng trồng + chứng nhận GAP
// 5. Ghi chú
//
// "Chủ sở hữu" KHÔNG nằm trong form này vì 2 nơi dùng khác nhau: admin chọn
// từ dropdown, nhân viên ngoài hiện trường thì chủ vườn đã cố định theo
// ngữ cảnh — nơi gọi tự render phần đó.
function FormVuon({ value, onChange, chuVuon, giongChuVuon, onChangeGiongChuVuon }) {
  const [loaiBoList, setLoaiBoList] = useState([]);
  const tenTuDongTruoc = useRef("");

  useEffect(() => {
    danhMucApi.loaiBo().then(setLoaiBoList);
  }, []);

  const set = (patch) => onChange({ ...value, ...patch });

  const hoTenChu = chuVuon?.hoTen ?? "";
  const chipDangChon = phanNguyenDienTich(value.dienTichHa);
  const tenVuonHienThi = taoTenVuonTuChuVaDienTich(hoTenChu, value.dienTichHa);

  // Chỉ auto-ghi đè tenVuon nếu đang trống hoặc vẫn theo công thức (không đè tên tùy chỉnh khi sửa).
  useEffect(() => {
    const tenMoi = taoTenVuonTuChuVaDienTich(hoTenChu, value.dienTichHa);
    const tenHien = String(value.tenVuon ?? "").trim();
    const truoc = tenTuDongTruoc.current;
    const dangTheoCongThuc = !tenHien || tenHien === truoc || tenHien === tenMoi;
    tenTuDongTruoc.current = tenMoi;
    if (dangTheoCongThuc && tenMoi !== tenHien) {
      onChange({ ...value, tenVuon: tenMoi });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoTenChu, value.dienTichHa]);

  function diaChiTuChuVuon() {
    return {
      tinh: chuVuon?.tinh ?? "",
      xa: chuVuon?.xa ?? "",
      thon: chuVuon?.thon ?? "",
      diaChiChiTiet: chuVuon?.diaChi ?? "",
    };
  }

  function chonKieuDiaChi(giong) {
    onChangeGiongChuVuon(giong);
    if (giong) set(diaChiTuChuVuon());
  }

  function toggleGiongBo(ten) {
    set({
      giongBo: value.giongBo.includes(ten)
        ? value.giongBo.filter((g) => g !== ten)
        : [...value.giongBo, ten],
    });
  }

  function chonMucDienTich(muc) {
    const tenTuDong = taoTenVuonTuChuVaDienTich(hoTenChu, muc);
    const tenHien = String(value.tenVuon ?? "").trim();
    const giuTenTuyChinh =
      tenHien && tenHien !== tenTuDongTruoc.current && tenHien !== tenVuonHienThi;
    tenTuDongTruoc.current = tenTuDong;
    set({ dienTichHa: muc, tenVuon: giuTenTuyChinh ? tenHien : tenTuDong });
  }

  function doiDienTichNhap(raw) {
    const parsed = parseDienTichHa(raw);
    const tenTuDong = taoTenVuonTuChuVaDienTich(hoTenChu, parsed);
    const tenHien = String(value.tenVuon ?? "").trim();
    const giuTenTuyChinh =
      tenHien && tenHien !== tenTuDongTruoc.current && tenHien !== tenVuonHienThi;
    tenTuDongTruoc.current = tenTuDong;
    set({ dienTichHa: parsed, tenVuon: giuTenTuyChinh ? tenHien : tenTuDong });
  }

  const soDienTich = Number(String(value.dienTichHa ?? "").replace(",", "."));
  const okDinhDanh =
    coNoiDung(hoTenChu) && !Number.isNaN(soDienTich) && soDienTich > 0 && coNoiDung(tenVuonHienThi);
  const okDiaChi = coNoiDung(value.tinh) && coNoiDung(value.xa);
  const okCanhTac = Array.isArray(value.giongBo) && value.giongBo.length > 0;
  // Chứng nhận / ghi chú: đủ sau khi bấm Tiếp tục.
  const okTuyChonPlaceholder = false;

  const trangThai = useMemo(
    () => ({
      dinhDanh: okDinhDanh,
      diaChi: okDiaChi,
      canhTac: okCanhTac,
      chungNhan: okTuyChonPlaceholder,
      ghiChu: okTuyChonPlaceholder,
    }),
    [okDinhDanh, okDiaChi, okCanhTac]
  );

  const { nhomMo, toggleNhom, khoaNhom, trangThaiHieuLuc } = useNhomFormMo(
    THU_TU_NHOM,
    trangThai,
    NHOM_TUY_CHON,
    Boolean(value.tenVuon)
  );

  return (
    <>
      <TieuDeNhomForm
        tieuDe="1. Định danh vườn"
        moTa="Tên tự ghép từ họ tên chủ và diện tích — chọn diện tích bên dưới để cập nhật."
        hoanThanh={okDinhDanh}
        mo={nhomMo === "dinhDanh"}
        khoa={khoaNhom("dinhDanh")}
        onToggle={() => toggleNhom("dinhDanh")}
      >
        <Grid item xs={12}>
          <MDTypography
            variant="caption"
            color="text"
            textTransform="uppercase"
            fontWeight="bold"
            mb={0.5}
            display="block"
          >
            Tên vườn *
          </MDTypography>
          {hoTenChu.trim() ? (
            <MDBox
              px={1.5}
              py={1.25}
              borderRadius="lg"
              sx={({ palette }) => ({
                backgroundColor: palette.grey[100],
                border: `1px solid ${palette.grey[300]}`,
              })}
            >
              <MDTypography variant="button" fontWeight="medium">
                {tenVuonHienThi || hoTenChu.trim()}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mt={0.25}>
                Tự ghép — không cần nhập tay
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox
              px={1.5}
              py={1.25}
              borderRadius="lg"
              sx={({ palette }) => ({
                backgroundColor: palette.grey[100],
                border: `1px dashed ${palette.grey[400]}`,
              })}
            >
              <MDTypography variant="button" color="text">
                Chọn chủ vườn trước
              </MDTypography>
            </MDBox>
          )}
        </Grid>

        <Grid item xs={12}>
          <MDTypography
            variant="caption"
            color="text"
            textTransform="uppercase"
            fontWeight="bold"
            mb={0.5}
            display="block"
          >
            Diện tích (ha)
          </MDTypography>
          <MDBox display="grid" gridTemplateColumns="repeat(5, minmax(0, 1fr))" gap={0.75} mb={1.5}>
            {MUC_DIEN_TICH.map((muc) => (
              <Chip
                key={muc}
                label={muc}
                size="small"
                color={chipDangChon === muc ? "info" : "default"}
                variant={chipDangChon === muc ? "filled" : "outlined"}
                onClick={() => chonMucDienTich(muc)}
                sx={{
                  width: "100%",
                  fontWeight: chipDangChon === muc ? 700 : 500,
                  "& .MuiChip-label": { px: 0.5, width: "100%", textAlign: "center" },
                }}
              />
            ))}
          </MDBox>
          <MDInput
            label="Điều chỉnh thập phân (vd 9,5)"
            value={formatSoThapPhanVn(value.dienTichHa)}
            onChange={(e) => doiDienTichNhap(e.target.value)}
            fullWidth
            inputProps={{ inputMode: "decimal", placeholder: "vd: 9,5" }}
          />
        </Grid>
      </TieuDeNhomForm>

      <TieuDeNhomForm
        tieuDe="2. Địa chỉ"
        moTa="Ưu tiên cùng địa chỉ chủ vườn; chỉ nhập lại khi vườn ở nơi khác."
        hoanThanh={okDiaChi}
        mo={nhomMo === "diaChi"}
        khoa={khoaNhom("diaChi")}
        onToggle={() => toggleNhom("diaChi")}
      >
        {chuVuon && (chuVuon.tinh || chuVuon.xa) && (
          <Grid item xs={12}>
            <RadioGroup
              row
              value={giongChuVuon ? "giong" : "khac"}
              onChange={(e) => chonKieuDiaChi(e.target.value === "giong")}
            >
              <FormControlLabel
                value="giong"
                control={<Radio />}
                label="Cùng địa chỉ với chủ vườn"
              />
              <FormControlLabel value="khac" control={<Radio />} label="Địa chỉ khác" />
            </RadioGroup>
          </Grid>
        )}

        <ChonDiaChi
          tinh={value.tinh}
          xa={value.xa}
          onChange={({ tinh, xa }) => set({ tinh, xa })}
          batBuoc
          disabled={giongChuVuon && !!chuVuon}
        />
        <Grid item xs={12} sm={6}>
          <MDInput
            label="Thôn/Ấp"
            value={value.thon ?? ""}
            onChange={(e) => set({ thon: e.target.value })}
            disabled={giongChuVuon && !!chuVuon}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label="Địa chỉ chi tiết"
            value={value.diaChiChiTiet ?? ""}
            onChange={(e) => set({ diaChiChiTiet: e.target.value })}
            disabled={giongChuVuon && !!chuVuon}
            fullWidth
          />
        </Grid>
      </TieuDeNhomForm>

      <TieuDeNhomForm
        tieuDe="3. Canh tác"
        moTa="Giống bơ bắt buộc; năm trồng và số cây thu thập thêm nếu có."
        hoanThanh={okCanhTac}
        mo={nhomMo === "canhTac"}
        khoa={khoaNhom("canhTac")}
        onToggle={() => toggleNhom("canhTac")}
      >
        <Grid item xs={12}>
          <MDTypography
            variant="caption"
            color="text"
            textTransform="uppercase"
            fontWeight="bold"
            mb={0.5}
            display="block"
          >
            Giống bơ đang trồng *
          </MDTypography>
          <MDBox display="flex" flexWrap="wrap" gap={1}>
            {loaiBoList.map((lb) => (
              <FormControlLabel
                key={lb.id}
                control={
                  <Checkbox
                    checked={value.giongBo.includes(lb.tenLoai)}
                    onChange={() => toggleGiongBo(lb.tenLoai)}
                  />
                }
                label={lb.tenLoai}
              />
            ))}
          </MDBox>
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            type="number"
            label="Năm bắt đầu trồng"
            value={value.namBatDauTrong ?? ""}
            onChange={(e) => set({ namBatDauTrong: e.target.value })}
            fullWidth
            inputProps={{ min: 1900, max: 2100 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            type="number"
            label="Số cây"
            value={value.soCay ?? ""}
            onChange={(e) => set({ soCay: e.target.value })}
            fullWidth
            inputProps={{ min: 0, step: 1 }}
          />
        </Grid>
      </TieuDeNhomForm>

      <TieuDeNhomForm
        tieuDe="4. Chứng nhận và truy xuất"
        moTa="Không bắt buộc — bấm Divider để đóng rồi mở mục tiếp."
        hoanThanh={trangThaiHieuLuc.chungNhan}
        mo={nhomMo === "chungNhan"}
        khoa={khoaNhom("chungNhan")}
        onToggle={() => toggleNhom("chungNhan")}
      >
        <Grid item xs={12} sm={6}>
          <MDInput
            label="Mã số vùng trồng"
            value={value.maSoVungTrong ?? ""}
            onChange={(e) => set({ maSoVungTrong: e.target.value })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label="Chứng nhận (VietGAP, GlobalGAP...)"
            value={value.chungNhan ?? ""}
            onChange={(e) => set({ chungNhan: e.target.value })}
            fullWidth
          />
        </Grid>
      </TieuDeNhomForm>

      <TieuDeNhomForm
        tieuDe="5. Ghi chú"
        moTa="Không bắt buộc — bấm Divider để đóng khi xong."
        hoanThanh={trangThaiHieuLuc.ghiChu}
        mo={nhomMo === "ghiChu"}
        khoa={khoaNhom("ghiChu")}
        onToggle={() => toggleNhom("ghiChu")}
      >
        <Grid item xs={12}>
          <MDInput
            label="Ghi chú"
            value={value.ghiChu ?? ""}
            onChange={(e) => set({ ghiChu: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
        </Grid>
      </TieuDeNhomForm>
    </>
  );
}

FormVuon.defaultProps = {
  chuVuon: null,
};

FormVuon.propTypes = {
  value: PropTypes.shape({
    tenVuon: PropTypes.string,
    tinh: PropTypes.string,
    xa: PropTypes.string,
    thon: PropTypes.string,
    diaChiChiTiet: PropTypes.string,
    dienTichHa: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    namBatDauTrong: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    soCay: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    giongBo: PropTypes.arrayOf(PropTypes.string).isRequired,
    maSoVungTrong: PropTypes.string,
    chungNhan: PropTypes.string,
    ghiChu: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  chuVuon: PropTypes.shape({
    hoTen: PropTypes.string,
    tinh: PropTypes.string,
    xa: PropTypes.string,
    thon: PropTypes.string,
    diaChi: PropTypes.string,
  }),
  giongChuVuon: PropTypes.bool.isRequired,
  onChangeGiongChuVuon: PropTypes.func.isRequired,
};

export default FormVuon;

export const RONG_VUON = {
  tenVuon: "",
  tinh: "",
  xa: "",
  thon: "",
  diaChiChiTiet: "",
  dienTichHa: "",
  namBatDauTrong: "",
  soCay: "",
  giongBo: [],
  maSoVungTrong: "",
  chungNhan: "",
  ghiChu: "",
};
